import { Component, OnInit, OnDestroy, HostListener, HostBinding, Input, AfterViewInit, ElementRef, Renderer2 } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ChatFireService } from '../../../gen2ai/chatfire.service';
import { AuthenticationService } from '../../../clari-auth/authentication.service';
import { UserProfile } from '../../../interfaces-types/user.interface';
import { Conversation } from '../../../models/chat.model';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ReasoningService, ReasoningLevel } from '../../../services/reasoning.service';
import {
  BETA_MENU_SECTIONS,
  BetaMenuLink,
  BetaMenuSection,
  betaMenuPathFromRouterUrl,
  betaSectionMatchesRoute
} from '../beta-menu/beta-menu-sections.config';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./styles/main-menu.component.scss']
})
export class MainMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Navigation mirrors `BETA_MENU_SECTIONS` by default (paths, labels, icons). */
  @Input() replicateBetaMenuLinks = true;

  @HostBinding('class.main-menu-beta-replica')
  get mainMenuBetaReplicaHostClass(): boolean {
    return this.replicateBetaMenuLinks;
  }

  @HostBinding('class.main-menu-collapsed-host')
  get mainMenuCollapsedHostClass(): boolean {
    return this.isMenuCollapsed;
  }

  /** Same two accordions as the beta menu (`BETA_MENU_SECTIONS`). */
  readonly betaMenuSections: readonly BetaMenuSection[] = BETA_MENU_SECTIONS;
  readonly authState$: Observable<boolean>;

  isMenuOpen = false;
  get isMenuCollapsed(): boolean {
    return !this.isMenuOpen;
  }

  toggleMenuOpen(): void {
    this.isMenuOpen = !this.isMenuOpen;
    this.syncParentSidenavWidth();
  }

  // Example dropdown state for sections
  expandedSections: Record<string, boolean> = {};

  /* =========================================
    Menu-item-title stays open (see below)
   ========================================= */
  sectionRoutes: Record<string, string[]> = {
    chat: [
      '/ai/clarichat',
      '/multi-ai/executive-chat',
      '/library/search'
    ],
    clarigen: [
      '/ai/videos',
      '/ai/images',
      '/notes',
      '/library/photo-lab'
    ],
    instruments: [
      '/library/calendar',
      '/ai/roster',
      '/multi-ai/iq',
      '/multi-ai/personality',
      '/ai/dashboard'
    ]
  };

  isRouteInSection(sectionId: string): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return this.sectionRoutes[sectionId]?.some(route =>
      currentUrl.startsWith(route)
    ) ?? false;
  }
  /* =========================================
    Menu-item-title stays open (see above)
   ========================================= */

  // Authentication state
  currentUser: UserProfile | null = null;
  isLoggedIn = false;
  userAvatar: string | null = null;
  avatarDefaultImg: string = '';
  showAvatarBuilder = false;

  // Conversation history
  recentConversations: Conversation[] = [];
  loadingConversations = false;
  hoveredConversationId: string | null = null;
  editingTopicId: string | null = null;
  editingTopicValue: string = '';

  // Reasoning level dropdown
  reasoningDropdownOpen = false;
  selectedReasoningLevel: ReasoningLevel = 'thinking';
  legacyModelsExpanded = false;
  showReasoningDropdown = false; // Will be set based on selected model

  private authSub!: Subscription;
  private loginSub!: Subscription;
  private reasoningSub!: Subscription;
  private routeSub?: Subscription;
  private betaReplicaLoginSub?: Subscription;
  private betaReplicaRouteSub?: Subscription;
  private isLoggedInChatReplica = false;

  constructor(
    private router: Router,
    private chatService: ChatFireService,
    private authService: AuthenticationService,
    private reasoningService: ReasoningService,
    private hostEl: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {
    this.authState$ = this.chatService.authState$;
    this.generateDefaultAvatar();
  }

  ngAfterViewInit(): void {
    this.syncParentSidenavWidth();
  }

  ngOnInit(): void {
    if (this.replicateBetaMenuLinks) {
      this.initBetaReplicaMenu();
      return;
    }

    // Subscribe to reasoning level changes
    this.reasoningSub = this.reasoningService.reasoningLevel$.subscribe(level => {
      this.selectedReasoningLevel = level;
    });
    this.selectedReasoningLevel = this.reasoningService.getReasoningLevel();

    // Subscribe to user changes
    this.authSub = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      this.userAvatar = (user as any)?.avatarUrl || null;

      if (user) {
        void this.loadRecentConversations();
      } else {
        this.recentConversations = [];
      }
    });

    // Subscribe to login status
    this.loginSub = this.authService.isLoggedIn$.subscribe(isLoggedIn => {
      this.isLoggedIn = isLoggedIn;

      if (isLoggedIn) {
        void this.loadRecentConversations();
      } else {
        this.recentConversations = [];
      }
    });

    // Refresh conversation list when route changes
    this.routeSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isLoggedIn) {
          setTimeout(() => {
            void this.loadRecentConversations();
          }, 500);
        }
      });
  }

  /**
   * Load up to 5 recent conversations for display in the menu
   */
  async loadRecentConversations(): Promise<void> {
    if (!this.isLoggedIn) {
      this.recentConversations = [];
      return;
    }

    try {
      this.loadingConversations = true;
      const conversations = await firstValueFrom(this.chatService.getRecentConversations(5));
      this.recentConversations = conversations;
    } catch (err) {
      console.error('[MainMenu] Error loading conversations', err);
      this.recentConversations = [];
    } finally {
      this.loadingConversations = false;
    }
  }

  /**
   * Load a conversation and navigate to chat
   */
  async loadConversation(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        await this.router.navigate(['/ai/clarichat'], {
          queryParams: { conversationId }
        });
      }
    } catch (err) {
      console.error('[MainMenu] Error loading conversation', err);
    }
  }

  /**
   * Start a new chat conversation - clears current session and starts fresh
   */
  async startNewChat(): Promise<void> {
    try {
      const newConversation = await firstValueFrom(this.chatService.createNewConversation());
      await this.router.navigate(['/ai/clarichat'], {
        queryParams: { conversationId: newConversation.id, newChat: 'true' }
      });
    } catch (err) {
      console.error('[MainMenu] Error starting new chat', err);
      await this.router.navigate(['/ai/clarichat']);
    }
  }

  /**
   * Format date for display
   */
  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  }

  getTruncatedEmail(email: string | undefined | null): string {
    if (!email) return '';
    return email.length > 12 ? email.substring(0, 12) + '...' : email;
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.loginSub?.unsubscribe();
    this.reasoningSub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.betaReplicaLoginSub?.unsubscribe();
    this.betaReplicaRouteSub?.unsubscribe();
  }

  private syncParentSidenavWidth(): void {
    const host = this.hostEl?.nativeElement;
    if (!host) return;

    const sidenav = host.closest('mat-sidenav.admin-main-sidenav') as HTMLElement | null;
    if (!sidenav) return;

    const openWidth = this.readCssPixelVar(host, '--main-menu-width', 220);
    const collapsedWidth = this.readCssPixelVar(host, '--main-menu-collapsed-width', 35);
    if (this.isMenuCollapsed) {
      this.renderer.setStyle(sidenav, 'width', 'fit-content');
      this.renderer.setStyle(sidenav, 'min-width', `${collapsedWidth}px`);
      this.renderer.setStyle(sidenav, 'max-width', 'fit-content');
    } else {
      this.renderer.setStyle(sidenav, 'width', 'fit-content');
      this.renderer.setStyle(sidenav, 'min-width', `${openWidth}px`);
      this.renderer.setStyle(sidenav, 'max-width', 'none');
    }
    // Keep the edge toggle visible when collapsed.
    this.renderer.setStyle(sidenav, 'overflow', 'visible');
  }

  private readCssPixelVar(host: HTMLElement, varName: string, fallback: number): number {
    const raw = getComputedStyle(host).getPropertyValue(varName).trim();
    const n = Number.parseFloat(raw.replace('px', ''));
    return Number.isFinite(n) ? n : fallback;
  }

  private initBetaReplicaMenu(): void {
    this.betaReplicaLoginSub = this.chatService.authState$.subscribe((isLoggedIn) => {
      this.isLoggedInChatReplica = isLoggedIn;
      if (isLoggedIn) {
        void this.loadRecentConversationsBetaReplica();
      } else {
        this.recentConversations = [];
      }
    });

    this.betaReplicaRouteSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isLoggedInChatReplica) {
          setTimeout(() => void this.loadRecentConversationsBetaReplica(), 500);
        }
      });
  }

  isRouteInBetaSection(section: BetaMenuSection): boolean {
    return betaSectionMatchesRoute(section, betaMenuPathFromRouterUrl(this.router.url));
  }

  trackByBetaSection(_index: number, section: BetaMenuSection): string {
    return section.id;
  }

  trackByBetaLink(index: number, link: BetaMenuLink): string {
    return `${link.route}-${index}`;
  }

  async loadRecentConversationsBetaReplica(): Promise<void> {
    if (!this.isLoggedInChatReplica) {
      this.recentConversations = [];
      return;
    }
    try {
      this.loadingConversations = true;
      const conversations = await firstValueFrom(this.chatService.getRecentConversations(6));
      this.recentConversations = this.filterDisplayableConversationsBetaReplica(conversations);
    } catch (err) {
      console.error('[MainMenu] Error loading conversations (beta replica)', err);
      this.recentConversations = [];
    } finally {
      this.loadingConversations = false;
    }
  }

  async loadConversationBetaReplica(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        await this.router.navigate(['/ai/clarichat'], { queryParams: { conversationId } });
      }
    } catch (err) {
      console.error('[MainMenu] Error loading conversation (beta replica)', err);
    }
  }

  truncateTopicBetaReplica(topic: string): string {
    if (!topic) return 'Classified Conversation';
    return topic.length <= 25 ? topic : `${topic.substring(0, 25)}...`;
  }

  onBetaReplicaAuthClick(event: Event, link?: BetaMenuLink): void {
    const isAuthLink = link?.label === 'Log In' || link?.route === '/auth/log-in';
    if (!isAuthLink || !this.isLoggedInChatReplica) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.chatService.logout().subscribe({
      next: () => this.afterBetaReplicaLogout(),
      error: () => this.afterBetaReplicaLogout()
    });
  }

  private afterBetaReplicaLogout(): void {
    this.isLoggedInChatReplica = false;
    this.recentConversations = [];
    void this.router.navigate(['/auth/log-in'], { queryParams: { loggedOut: 'true' } });
  }

  private filterDisplayableConversationsBetaReplica(conversations: Conversation[]): Conversation[] {
    return conversations.filter((conversation) => {
      const messages = conversation.messages ?? [];
      if (messages.length < 2) return false;
      return messages.some((m) => m.role === 'user') && messages.some((m) => m.role === 'assistant');
    });
  }

  private generateDefaultAvatar(): void {
    const style = 'bottts';
    const seed = 'Simms';
    const backgroundColor = '1a1aff';
    const primaryColor = '00bfff';

    const params = new URLSearchParams({
      seed,
      backgroundColor,
      primaryColor
    });

    this.avatarDefaultImg = `https://api.dicebear.com/9.x/${style}/svg?${params.toString()}`;
  }

  toggleAvatarBuilder(): void {
    this.showAvatarBuilder = !this.showAvatarBuilder;
  }

  onAvatarFinished(avatarUrl: string): void {
    this.userAvatar = avatarUrl;
    this.showAvatarBuilder = false;
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.chatService.logout());
    } catch (err) {
      console.error('[MainMenu] Logout error', err);
    } finally {
      this.currentUser = null;
      this.isLoggedIn = false;
      this.userAvatar = null;
      this.recentConversations = [];
      await this.router.navigate(['/auth/log-in'], {
        queryParams: { loggedOut: 'true' }
      });
    }
  }

  /** Explicitly open menu */
  openMenu(): void {
    this.isMenuOpen = true;
  }

  /** Explicitly close menu */
  closeMenu(): void {
    this.isMenuOpen = false;
  }

  /** Expand or collapse a specific section by ID */
  expandSection(sectionId: string): void {
    this.expandedSections[sectionId] = true;
  }

  collapseSection(sectionId: string): void {
    this.expandedSections[sectionId] = false;
  }

  toggleSection(sectionId: string): void {
    this.expandedSections[sectionId] = !this.expandedSections[sectionId];
  }

  /** Utility: check if section is expanded */
  isExpanded(sectionId: string): boolean {
    return !!this.expandedSections[sectionId];
  }

  /**
   * Track by function for conversation list
   */
  trackByConversationId(index: number, conv: Conversation): string {
    return conv.id;
  }

  /**
   * Truncate topic to 20 characters with ellipsis
   */
  truncateTopic(topic: string): string {
    if (!topic) return 'Conversation';
    if (topic.length <= 20) return topic;
    return topic.substring(0, 20) + '...';
  }

  /**
   * Truncate menu item text to 12 characters with ellipsis
   * Ellipsis starts at index 13 (after 12 characters)
   */
  truncateMenuItem(text: string): string {
    if (!text) return '';
    if (text.length <= 12) return text;
    return text.substring(0, 12) + '...';
  }

  /**
   * Start editing a conversation topic
   */
  startEditingTopic(conversationId: string, currentTopic: string): void {
    this.editingTopicId = conversationId;
    this.editingTopicValue = currentTopic || '';

    setTimeout(() => {
      const input = document.querySelector('input.conversation-topic-input') as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      }
    }, 50);
  }

  /**
   * Save the edited topic
   */
  async saveTopic(conversationId: string): Promise<void> {
    if (!this.editingTopicId || !this.editingTopicValue.trim()) {
      this.cancelEditingTopic();
      return;
    }

    try {
      const conversation = this.recentConversations.find(c => c.id === conversationId);
      if (conversation) {
        conversation.topic = this.editingTopicValue.trim();

        await firstValueFrom(this.chatService.updateConversation({
          ...conversation,
          topic: this.editingTopicValue.trim()
        }));

        if (this.replicateBetaMenuLinks) {
          await this.loadRecentConversationsBetaReplica();
        } else {
          await this.loadRecentConversations();
        }
      }
    } catch (err) {
      console.error('[MainMenu] Error saving topic', err);
    } finally {
      this.cancelEditingTopic();
    }
  }

  /**
   * Cancel editing topic
   */
  cancelEditingTopic(): void {
    this.editingTopicId = null;
    this.editingTopicValue = '';
  }

  // ===================== Reasoning Level Dropdown =====================

  toggleReasoningDropdown(): void {
    this.reasoningDropdownOpen = !this.reasoningDropdownOpen;
  }

  selectReasoningLevel(level: ReasoningLevel): void {
    this.reasoningService.setReasoningLevel(level);
    this.reasoningDropdownOpen = false;
  }

  getReasoningLabel(level: ReasoningLevel): string {
    const labels = {
      auto: 'Auto',
      instant: 'Instant',
      thinking: 'Thinking',
      pro: 'Pro'
    };
    return labels[level] || 'Thinking';
  }

  toggleLegacyModels(): void {
    this.legacyModelsExpanded = !this.legacyModelsExpanded;
  }

  openAddCredits(): void {
    console.log('Open credits page');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.reasoning-dropdown')) {
      this.reasoningDropdownOpen = false;
    }

    const clickedInsideMenu = this.hostEl?.nativeElement?.contains(target) ?? false;
    if (this.isMenuOpen && !clickedInsideMenu) {
      this.isMenuOpen = false;
      this.syncParentSidenavWidth();
    }
  }
}
